// =============================================================================
// HudView.ts — HUD em jogo: timer, XP/nível da chapa, HP, arsenal, party no
// canto, barra de boss, feed lateral e a vinheta de dano.
// Updates são incrementais: roda a 12.5Hz, então só toca no DOM quando o
// valor realmente mudou (rebuild de innerHTML por snapshot matava o FPS).
// =============================================================================
import { WEAPON_TABLE } from '../../../shared/weapons';
import { PSTATE, type GameStartPlayer, type Snapshot } from '../../../shared/protocol';
import { abilityIcon, killsIcon, playerStateIcon } from './pixelIcons';
import { $, charImg } from './dom';

// Refs persistentes de uma linha da party + últimos valores escritos.
// killsText é o nó de texto DEPOIS da espadinha pixel art: só o número muda
// por snapshot — o SVG do ícone nunca é re-renderizado
interface PartyRow {
  row: HTMLDivElement;
  status: HTMLSpanElement;
  killsText: Text;
  hpFill: HTMLDivElement;
  lastDowned: boolean;
  lastStatus: string;
  lastKills: string;
  lastHpW: string;
}

export class HudView {
  // Últimos valores escritos no DOM (evita writes redundantes por snapshot)
  private lastTimer = '';
  private lastInfinite = '';
  private lastXpW = '';
  private lastXpText = '';
  private lastLevel = '';
  private lastHpW = '';
  private lastHpText = '';
  private lastC2 = '';
  private lastFrozen = '';
  private lastWeaponsKey = '';
  private lastBossShown: boolean | null = null;
  private lastBossW = '';
  private lastPartyKey = '';
  private readonly partyRows = new Map<string, PartyRow>();
  private hintTimer: ReturnType<typeof setTimeout> | undefined;
  // Toast de Promessa de Campanha (#99): fila pra duas conquistas no mesmo
  // segundo não se atropelarem no canto da tela
  private toastQueue: { nome: string; votos: number }[] = [];
  private toastBusy = false;

  update(snap: Snapshot, youId: string | null, playersInfo: Map<string, GameStartPlayer>, duration: number): void {
    const me = snap.players.find((p) => p.id === youId);

    // Timer regressivo até o fim da apuração; passou dos 20min (#85)? O relógio
    // vira progressivo e o selo MODO INFINITO acende — o mandato é vitalício
    const infinite = snap.t >= duration;
    const shown = infinite ? snap.t : Math.max(duration - snap.t, 0);
    const mm = String(Math.floor(shown / 60)).padStart(2, '0');
    const ss = String(Math.floor(shown % 60)).padStart(2, '0');
    const timer = `${mm}:${ss}`;
    if (timer !== this.lastTimer) { this.lastTimer = timer; $('hud-timer').textContent = timer; }
    const infDisplay = infinite ? '' : 'none';
    if (infDisplay !== this.lastInfinite) {
      this.lastInfinite = infDisplay;
      $('hud-infinite').style.display = infDisplay;
    }

    // XP e nível são da chapa inteira (todo mundo sobe junto)
    const xpW = `${(snap.xp / snap.xpNeed) * 100}%`;
    if (xpW !== this.lastXpW) { this.lastXpW = xpW; $('hud-xp-fill').style.width = xpW; }
    const xpText = `VOTOS DA CHAPA ${snap.xp}/${snap.xpNeed}`;
    if (xpText !== this.lastXpText) { this.lastXpText = xpText; $('hud-xp-text').textContent = xpText; }
    const level = `Nv ${snap.lvl}`;
    if (level !== this.lastLevel) { this.lastLevel = level; $('hud-level').textContent = level; }

    if (me) {
      const hpW = `${(me.hp / me.maxHp) * 100}%`;
      if (hpW !== this.lastHpW) { this.lastHpW = hpW; $('hud-hp-fill').style.width = hpW; }
      const hpText = `${me.hp} / ${me.maxHp}`;
      if (hpText !== this.lastHpText) { this.lastHpText = hpText; $('hud-hp-text').textContent = hpText; }
      const frozen = me.frz && me.st === PSTATE.ALIVE ? 'block' : 'none';
      if (frozen !== this.lastFrozen) { this.lastFrozen = frozen; $('frozen-overlay').style.display = frozen; }

      // Caixa 2 (#94): contador pessoal — só o SEU bolso aparece no seu HUD
      const c2 = String(me.c2 ?? 0);
      if (c2 !== this.lastC2) { this.lastC2 = c2; $('hud-caixa2-num').textContent = c2; }

      // Arsenal: só rebuilda quando o loadout muda (pickup/upgrade)
      const wkey = me.wp.map(([wid, lvl]) => wid + lvl).join('|');
      if (wkey !== this.lastWeaponsKey) {
        this.lastWeaponsKey = wkey;
        const wbox = $('hud-weapons');
        wbox.innerHTML = '';
        for (const [wid, lvl] of me.wp) {
          const w = WEAPON_TABLE[wid];
          if (!w) continue;
          const slot = document.createElement('div');
          // Arma suprema (#98): moldura dourada + estrela no lugar do nível
          slot.className = 'wslot' + (w.evolved ? ' evo' : '');
          slot.title = w.evolved ? `${w.nome} (SUPREMA)` : `${w.nome} (nv ${lvl})`;
          slot.innerHTML = `${abilityIcon('weapon', wid, 24)}<small>${w.evolved ? '★' : lvl}</small>`;
          wbox.appendChild(slot);
        }
      }
    }

    // Chapa (party) no canto: linhas persistentes por jogador; rebuild total
    // só quando o conjunto de ids muda (novo jogo)
    const partyKey = snap.players.map((p) => p.id).join('|');
    if (partyKey !== this.lastPartyKey) {
      this.lastPartyKey = partyKey;
      this.buildParty(snap.players, playersInfo);
    }
    for (const p of snap.players) {
      const r = this.partyRows.get(p.id);
      if (!r) continue;
      const downed = p.st === PSTATE.DOWNED;
      if (downed !== r.lastDowned) { r.lastDowned = downed; r.row.classList.toggle('downed', downed); }
      // Estado do jogador em pixel art (issue #31): caveira (caído), zumbi
      // (virou base hostil) ou caixão (fim de mandato)
      const st = downed ? 'downed' as const : p.st === PSTATE.ZOMBIE ? 'zombie' as const
        : p.st === PSTATE.DEAD ? 'dead' as const : '';
      if (st !== r.lastStatus) {
        r.lastStatus = st;
        r.status.innerHTML = st ? ` ${playerStateIcon(st, 12)}` : '';
      }
      const kills = ` ${p.k}`;
      if (kills !== r.lastKills) { r.lastKills = kills; r.killsText.data = kills; }
      const hpW = `${(p.hp / p.maxHp) * 100}%`;
      if (hpW !== r.lastHpW) { r.lastHpW = hpW; r.hpFill.style.width = hpW; }
    }

    // Barra do boss
    const bossShown = !!snap.boss;
    if (bossShown !== this.lastBossShown) {
      this.lastBossShown = bossShown;
      $('hud-boss').style.display = bossShown ? '' : 'none';
    }
    if (snap.boss) {
      const bossW = `${(snap.boss.hp / snap.boss.maxHp) * 100}%`;
      if (bossW !== this.lastBossW) { this.lastBossW = bossW; $('hud-boss-fill').style.width = bossW; }
    }
  }

  // (Re)monta as linhas da party — descarta linhas de jogos anteriores
  private buildParty(players: Snapshot['players'], playersInfo: Map<string, GameStartPlayer>): void {
    const party = $('hud-party');
    party.innerHTML = '';
    this.partyRows.clear();
    for (const p of players) {
      const info = playersInfo.get(p.id);
      const row = document.createElement('div');
      row.className = 'party-row';
      row.insertAdjacentHTML('beforeend', `${charImg(info?.charId ?? 'lula', 22, false, info?.skinId)} ${info?.name ?? '?'}`);
      const status = document.createElement('span');
      row.appendChild(status);
      row.appendChild(document.createTextNode(' '));
      const kills = document.createElement('span');
      kills.style.color = '#f7d117';
      kills.innerHTML = killsIcon(11);          // espadinha fixa; só o número atualiza
      const killsText = document.createTextNode('');
      kills.appendChild(killsText);
      row.appendChild(kills);
      const php = document.createElement('div');
      php.className = 'php';
      const hpFill = document.createElement('div');
      php.appendChild(hpFill);
      row.appendChild(php);
      party.appendChild(row);
      this.partyRows.set(p.id, {
        row, status, killsText, hpFill,
        lastDowned: false, lastStatus: '', lastKills: '', lastHpW: '',
      });
    }
  }

  setBossName(nome: string): void {
    $('hud-boss-name').textContent = nome;
  }

  // Selo das eleições com seed (#101/#97) sob o relógio: modo + aviso de
  // amistoso quando a tentativa não pontua. null = partida normal (esconde).
  setEleicaoBadge(texto: string | null): void {
    const el = $('hud-eleicao');
    el.textContent = texto ?? '';
    el.style.display = texto ? '' : 'none';
  }

  // Feed lateral (bordões de boss, mortes, revives, level ups da chapa)
  feed(text: string, boss = false): void {
    const el = document.createElement('div');
    el.className = 'feed-item' + (boss ? ' boss' : '');
    el.textContent = text;
    const box = $('hud-feed');
    box.prepend(el);
    while (box.children.length > 6) box.lastChild?.remove();
    setTimeout(() => el.remove(), 7000);
  }

  hurtFlash(): void {
    const v = $('hurt-vignette');
    v.classList.add('on');
    setTimeout(() => v.classList.remove('on'), 120);
  }

  // Toast pixel art discreto de conquista (#99): desliza do topo, segura uns
  // segundos e some — sem cobrir o meio da tela no meio da horda
  conquistaToast(nome: string, votos: number): void {
    this.toastQueue.push({ nome, votos });
    if (!this.toastBusy) this.nextToast();
  }

  private nextToast(): void {
    const item = this.toastQueue.shift();
    if (!item) { this.toastBusy = false; return; }
    this.toastBusy = true;
    $('hud-conquista-nome').textContent = item.nome;
    $('hud-conquista-info').textContent = `+${item.votos.toLocaleString('pt-BR')} votos vitalícios`;
    const el = $('hud-conquista');
    el.style.display = '';
    // reflow antes da classe pra transição de entrada disparar sempre
    void el.offsetWidth;
    el.classList.add('show');
    setTimeout(() => {
      el.classList.remove('show');
      setTimeout(() => {
        el.style.display = 'none';
        this.nextToast();
      }, 350);
    }, 4200);
  }

  // Lembrete de controles no começo da partida: mostra por alguns segundos e
  // some com fade. Em tela touch o CSS esconde (o direcional virtual ensina).
  showControlsHint(): void {
    clearTimeout(this.hintTimer);
    const el = $('controls-hint');
    el.classList.add('show');
    this.hintTimer = setTimeout(() => el.classList.remove('show'), 9000);
  }
}
